import React, { ReactNode } from 'react'

import { OptionProps } from 'components_new/molecules/OptionsMenu'

import SegmentMenuSubContent from './SegmentMenuSubContent'

import * as UserEvents from 'redux/api/internal/UserEvents'

import type { TAttributeOptionDto } from '@homepal/api-contract'
import { Action, Context } from 'types/GlobalUserEvents'
import {
  ParsedSegmentPath,
  TWidgetSettings,
  WidgetType
} from 'types/GlobalWidget'
import { DETAILS_DISABLE_TEXT } from 'utils/constants'
import { TFunction } from 'language/translator'

/**
 * Get all available segment options.
 * @param attributeOptionId - Current segment attribute option id.
 * @param segmentOptions - All available segmentation options for widget.
 * @returns - Available segmentations for "Dela upp efter".
 */
const getAvailableSegmentOptions = (
  customSegmentBy: string,
  segmentOptions: TAttributeOptionDto[]
): TAttributeOptionDto[] => {
  const selectedOption = segmentOptions.find(
    (option) => option.id === customSegmentBy
  )

  if (!selectedOption) {
    return []
  }

  return segmentOptions.filter(
    (option) => option.allow_custom_segment_by && option.id !== customSegmentBy
  )
}

const getSegmentOptionsMenuContent = (
  segments: ParsedSegmentPath[],
  segmentOptions: TAttributeOptionDto[],
  setCustomSegmentBy: (
    attributeOptionId: string,
    segments: ParsedSegmentPath[]
  ) => void
): ReactNode => {
  const clickedSegment = segments[segments.length - 1]
  const segmentAttributeOptionId = clickedSegment.attribute_option_id as string

  const customSegmentOptions = getAvailableSegmentOptions(
    segmentAttributeOptionId,
    segmentOptions
  )

  if (customSegmentOptions.length === 0) {
    return null
  }

  return (
    <SegmentMenuSubContent
      segmentOptions={customSegmentOptions}
      setCustomSegmentBy={(attributeOptionId) => {
        setCustomSegmentBy(attributeOptionId, segments)
      }}
    />
  )
}

export const getOptions = (
  dashboardId: string,
  settings: TWidgetSettings,
  segments: ParsedSegmentPath[],
  kpiOptionId: string | null,
  segmentOptions: TAttributeOptionDto[],
  setFilters: (segments: ParsedSegmentPath[]) => void,
  t: TFunction,
  setDetailsDialog?:
    | ((segments: ParsedSegmentPath[], kpiOptionId?: string | null) => void)
    | null,
  customSegmentBy?: string | null,
  setCustomSegmentBy?:
    | ((attributeOptionId: string, segments: ParsedSegmentPath[]) => void)
    | null,
  onClickExternalLink?: ((link: string | null) => void) | null,
  link: string | null = null,
  isPublic?: boolean
): OptionProps[] => {
  const isHorizontalTable =
    settings.type.selected === WidgetType.LIST && settings.horizontal

  const segmentContent =
    !isHorizontalTable && customSegmentBy !== undefined && setCustomSegmentBy
      ? getSegmentOptionsMenuContent(
        segments,
        segmentOptions,
        setCustomSegmentBy
      )
      : null

  const getCustomSegmentDisabledTitle = () => {
    if (isPublic) {
      return t('widgets', 'login_required')
    } else if (!segmentContent) {
      return t('widgets', 'no_further_breakdown')
    } else {
      return undefined
    }
  }

  const getDetailsDisabledTitle = () => {
    if (isPublic) {
      return t('widgets', 'login_required')
    } else if (!Boolean(setDetailsDialog)) {
      return DETAILS_DISABLE_TEXT
    } else {
      return undefined
    }
  }

  return [
    {
      // a row standing for a metric rather than a segment value has
      // nothing to filter on
      disabled: segments.length === 0,
      iconName: 'Filter' as const,
      onClick: () => setFilters(segments),
      title: t('filters', 'add_to_filters')
    },
    {
      disabled: isPublic || !Boolean(setDetailsDialog),
      iconName: 'Explore' as const,
      infoTip: getDetailsDisabledTitle(),
      onClick: setDetailsDialog
        ? () => setDetailsDialog(segments, kpiOptionId)
        : () => {},
      title: t('global', 'explore')
    },
    // if customSegmentBy is undefined then dont show option
    // e.g. for line charts
    ...(customSegmentBy !== undefined
      ? [
          {
            disabled: isPublic || !segmentContent,
            iconName: 'AutoFixHighOutlined' as const,
            infoTip: getCustomSegmentDisabledTitle(),
            onClick: () => {},
            title: t('widgets', 'breakdown_by_placeholder'),
            content: segmentContent
          }
        ]
      : []),
    ...(onClickExternalLink
      ? [
          {
            disabled: !link,
            iconName: 'LinkOutlined' as const,
            onClick: () => {
              UserEvents.log({
                action: Action.NAVIGATED_TO_SOURCE,
                context: Context.FROM_WIDGET,
                dashboard_id: dashboardId,
                dashboard_group_id: null
              })

              onClickExternalLink(link)
            },
            title: link
              ? t('widgets', 'open_in_source_system')
              : t('widgets', 'missing_source_system')
          }
        ]
      : [])
  ]
}
